import { useEffect, useState } from "react";
import {
    getTenantViewCloudState,
    loadTenantViewCloud,
    subscribeTenantViewCloud,
} from "../lib/tenantViewCloudStore.js";
import { recoverPendingTenantViewCreates } from "../lib/tenantViewPendingCreateRecovery.js";

function readState(tenantId) {
    return getTenantViewCloudState(tenantId);
}

export default function TenantViewCloudNotice({ tenantId = null }) {
    const [state, setState] = useState(() => readState(tenantId));
    const [retrying, setRetrying] = useState(false);
    const [retryError, setRetryError] = useState(null);

    useEffect(() => {
        setState(readState(tenantId));
        return subscribeTenantViewCloud(() => setState(readState(tenantId)));
    }, [tenantId]);

    const unavailable = state.status === "unavailable";
    if (!unavailable && !retrying && !retryError) return null;

    const retry = async () => {
        if (retrying) return;
        setRetrying(true);
        setRetryError(null);
        try {
            const requestedTenantId = state.tenantId || tenantId;
            const refreshed = await loadTenantViewCloud(requestedTenantId, { force: true });
            if (refreshed.status === "ready") {
                if (!refreshed.tenantId) {
                    throw new Error("Cloud Studio reconnected without a tenant context.");
                }
                await recoverPendingTenantViewCreates({ tenantId: refreshed.tenantId });
            }
        } catch (error) {
            setRetryError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setRetrying(false);
        }
    };
    const detail = retryError?.message
        || state.error?.message
        || String(state.error || "");

    return (
        <div
            role="alert"
            className="mb-[8px] flex items-start justify-between gap-[12px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]"
        >
            <span>
                {retryError
                    ? "Cloud Studio retry failed. Local changes remain on this device."
                    : "Cloud Studio is unavailable. Changes stay on this device until the cloud catalog reconnects."}
                {detail ? ` ${detail}` : ""}
            </span>
            <button
                type="button"
                onClick={retry}
                disabled={retrying}
                className="shrink-0 border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[3px] text-[10px] text-[color:var(--ink)] disabled:opacity-60"
            >
                {retrying ? "Retrying…" : "Retry cloud"}
            </button>
        </div>
    );
}
